import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, ArrowUpRight, MessageCircle, User, FileText, Receipt, Users } from "lucide-react";
import { AccountUsagePrompt } from "@/components/intermediary/AccountUsagePrompt";
import { api, useAuth } from "@/lib/auth";
import { STATUS_LABELS, eur } from "@/lib/constants";

const STATUS_CLASS = (s) => s === "termine" ? "text-emerald-700" : s === "annule" ? "text-neutral-500" : "text-[#0E7C90]";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)); }, []);
  const active = orders.filter((o) => !["termine", "annule"].includes(o.status));
  const done = orders.filter((o) => o.status === "termine");
  const quotes = orders.filter((o) => o.type === "quote_request");
  const forClients = orders.filter((o) => o.end_client);
  const intermediary = ["clients", "both"].includes(user?.account_usage);

  return (
    <div className="cs-cream min-h-[80vh] py-14" data-testid="page-dashboard">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="cs-eyebrow text-neutral-500">Espace client</div>
            <h1 className="cs-h1 mt-3 text-4xl lg:text-5xl">Bonjour {user?.first_name || "—"}.</h1>
            <p className="mt-2 text-neutral-600 text-sm font-mono">{user?.email}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {user?.role === "admin" && <Link to="/admin" className="cs-btn-ghost !text-black !border-black" data-testid="dashboard-admin-link">Back-office</Link>}
            <Link to="/services" className="cs-btn-primary" data-testid="dashboard-new-order">Nouvelle commande <ArrowUpRight size={16} /></Link>
            <Link
              to="/espace/contact"
              data-testid="dashboard-contact-studio"
              className="inline-flex items-center gap-2 px-4 py-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
              title="Questions générales & assistance"
            >
              <MessageCircle size={16} /> Contacter le studio
            </Link>
            <Link
              to="/espace/clients"
              data-testid="dashboard-clients"
              className="inline-flex items-center gap-2 px-4 py-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
              title="Mes clients finaux"
            >
              <Users size={16} /> Mes clients
            </Link>
            <Link
              to="/espace/factures"
              data-testid="dashboard-invoices"
              className="inline-flex items-center gap-2 px-4 py-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
              title="Mes factures"
            >
              <Receipt size={16} /> Mes factures
            </Link>
            <Link
              to="/espace/facturation"
              data-testid="dashboard-billing"
              className="inline-flex items-center gap-2 px-4 py-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
              title="Informations de facturation"
            >
              <FileText size={16} /> Informations de facturation
            </Link>
            <Link
              to="/espace/profil"
              data-testid="dashboard-profile"
              className="inline-flex items-center gap-2 px-4 py-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors text-sm"
              title="Mon profil & sécurité"
            >
              <User size={16} /> Mon profil
            </Link>
            <button onClick={logout} className="p-3 border border-black/70 hover:bg-black hover:text-[#FAF8F5] transition-colors" data-testid="logout-btn" aria-label="Déconnexion"><LogOut size={16} /></button>
          </div>
        </div>

        <AccountUsagePrompt />

        <div className={`mt-10 grid gap-4 ${intermediary ? "md:grid-cols-4" : "md:grid-cols-3"}`}>
          <Stat label="En cours" value={active.length} />
          <Stat label="Devis" value={quotes.length} />
          <Stat label="Terminées" value={done.length} />
          {intermediary && <Stat label="Pour mes clients" value={forClients.length} />}
        </div>

        <section className="mt-14">
          <div className="cs-eyebrow text-neutral-500">Toutes mes commandes</div>
          <h2 className="cs-h3 mt-2 text-2xl">Historique</h2>
          {orders.length === 0 ? (
            <div className="mt-6 cs-card p-8 text-center">
              <p className="text-neutral-600">Aucune commande pour l'instant.</p>
              <Link to="/services" className="cs-btn-primary mt-6 inline-flex">Commander une prestation <ArrowUpRight size={16} /></Link>
            </div>
          ) : (
            <div className="mt-6 border-t border-black/15">
              {orders.map((o) => (
                <Link key={o.id} to={`/espace/commande/${o.id}`}
                  className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-6 border-b border-black/15 py-5 px-2 hover:bg-white transition-colors"
                  data-testid={`dashboard-order-${o.reference}`}>
                  <div className="font-mono text-xs text-neutral-500 w-24">{o.reference}</div>
                  <div>
                    <div className="font-serif text-xl">{o.service_name}</div>
                    {o.end_client && <div className="text-[12px] font-mono text-neutral-500" data-testid={`dashboard-order-client-${o.reference}`}>Pour {o.end_client.name}{o.billing_party === "end_client" ? " · facturé au client" : ""}</div>}
                    <div className={`text-xs font-mono mt-1 ${STATUS_CLASS(o.status)}`}>
                      <span className="cs-status-dot" style={{ background: o.status === "termine" ? "#16a34a" : "#0E7C90" }} />
                      {STATUS_LABELS[o.status] || o.status}
                    </div>
                  </div>
                  <div className="text-right hidden md:block">
                    <div className="cs-eyebrow text-neutral-500">Prix</div>
                    <div className="font-mono">{eur(o.price)}</div>
                  </div>
                  <ArrowUpRight size={18} />
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="cs-card p-6">
      <div className="cs-eyebrow text-neutral-500">{label}</div>
      <div className="font-serif text-5xl mt-2">{value}</div>
    </div>
  );
}
